import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';

import { useConfig } from '../ConfigContext';
import { PageConfig } from '../pages/types';

const TopNavigation = () => {
  const theme = useTheme();
  const config = useConfig();
  const navConfig = config?.config?.navigationConfig || {};

  const pages = config?.config?.pages;
  const location = useLocation();

  if (!pages) {
    return null;
  }

  const [mobileOpen, setMobileOpen] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(window.scrollY);
  const [selectedPage, setSelectedPage] = useState(location.pathname);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleScroll = () => {
    const currentScrollPos = window.scrollY;
    setHideNav(prevScrollPos < currentScrollPos && currentScrollPos > 50);
    setPrevScrollPos(currentScrollPos);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollPos]);

  useEffect(() => {
    setSelectedPage(location.pathname);
    window.scrollTo(0, 0); // Scroll to top when the selected page changes
  }, [location.pathname]);

  // Filter pages to only show those with showInNavigation not set to false
  // Also exclude the "Home" page since it's already accessible via the logo/home button
  const visiblePages: Record<string, PageConfig> = Object.entries(pages)
    .filter(([key, pageData]) => {
      // Skip the Home page in regular navigation
      // if (key === 'Home') return false;

      // Only show pages that have showInNavigation not explicitly set to false
      return pageData.showInNavigation !== false;
    })
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const pageKeys = Object.keys(visiblePages);

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: 'center',
        zIndex: 1000,
        width: '99vw',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center', // This centers content vertically
      }}
    >
      {/* Close button in the top right corner */}
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          color: 'primary',
        }}
        aria-label="close menu"
      >
        <CloseIcon />
      </IconButton>
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {pageKeys.map((pageKey) => (
          <ListItem
            key={pageKey}
            sx={{
              justifyContent: 'center',
              width: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Button
              component={Link}
              to={pageKey === 'Home' ? '/' : `/${pageKey}`}
              color="inherit"
              sx={{
                textTransform: 'none',
                fontWeight: selectedPage === `/${pageKey}` ? 'bold' : 'normal',
              }}
              onClick={handleDrawerToggle}
            >
              {visiblePages[pageKey].menutitle}
            </Button>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderNavItem = (pageKey: string, pageConfig: PageConfig) => {
    // Don't show in navigation if explicitly set to false
    if (pageConfig.showInNavigation === false) {
      return null;
    }

    // Check if page has subpages that should be displayed
    const hasVisibleSubpages =
      pageConfig.subpages &&
      Object.entries(pageConfig.subpages).some(
        ([_, subpage]) => subpage.showInNavigation !== false
      );

    // If page has subpages, create a dropdown menu
    if (hasVisibleSubpages) {
      return (
        <Box
          key={pageKey}
          sx={{
            position: 'relative',
            display: 'inline-block',
            '&:hover .subpage-menu': {
              display: 'block',
            },
          }}
        >
          <Button
            component={Link}
            to={pageKey === 'Home' ? '/' : `/${pageKey}`}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            {pageConfig.menutitle}
          </Button>

          <Box
            className="subpage-menu"
            sx={{
              display: 'none',
              position: 'absolute',
              backgroundColor: 'background.paper',
              boxShadow: 2,
              zIndex: 1000,
              minWidth: '160px',
            }}
          >
            {pageConfig.subpages &&
              Object.entries(pageConfig.subpages)
                .filter(([_, subpage]) => subpage.showInNavigation !== false)
                .map(([subpageKey, subpage]: [string, PageConfig]) => (
                  <Button
                    key={subpageKey}
                    component={Link}
                    to={`/${pageKey}/${subpageKey}`}
                    color="inherit"
                    sx={{
                      textTransform: 'none',
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 16px',
                    }}
                  >
                    {subpage.menutitle}
                  </Button>
                ))}
          </Box>
        </Box>
      );
    }

    // Regular page without subpages
    return (
      <Button
        key={pageKey}
        component={Link}
        to={pageKey === 'Home' ? '/' : `/${pageKey}`}
        color="inherit"
        sx={{ textTransform: 'none' }}
      >
        {pageConfig.menutitle}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: '100%',
        zIndex: 1000,
        position: 'fixed',
        pointerEvents: 'none',
        transition: 'top 0.3s',
        top: hideNav ? '-220px' : '0',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <AppBar
        position="sticky"
        className="app-bar"
        sx={{
          boxShadow: 'none',
          height: { xs: 70, sm: 80, md: 100 },
        }}
      >
        <Toolbar
          sx={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {/* Logo and/or Home Link */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Logo if available and showLogo is not false */}
            {navConfig.logo?.url && navConfig.showLogo !== false && (
              <Box
                sx={{
                  flexGrow: 0,
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Link to={navConfig.homeLink || '/'}>
                  <img
                    src={navConfig.logo.url}
                    alt={navConfig.logo.alt || 'Logo'}
                    style={{ height: navConfig.logo.height || '40px' }}
                  />
                </Link>
              </Box>
            )}

            {/* Home text button - show if showHomeText is true */}
            {navConfig.showHomeText && (
              <Button
                color="inherit"
                component={Link}
                to="/"
                sx={{
                  fontWeight: selectedPage === '/' ? 'bold' : 'normal',
                  textDecoration: selectedPage === '/' ? 'bold' : 'none',
                }}
              >
                {pages.Home?.menutitle || 'Home'}
              </Button>
            )}
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ ml: 'auto', display: { md: 'none' } }} // Show only on small screens
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop Navigation */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {config?.config?.pages &&
              pageKeys.map((pageKey, index, array) => {
                // Check if this is the last item and should be a button
                const isLast = index === array.length - 1;
                const showAsButton = isLast && navConfig.lastItemIsButton;

                // Get the page config
                const pageConfig = visiblePages[pageKey];

                // If page has subpages, render the dropdown menu
                if (
                  pageConfig.subpages &&
                  Object.keys(pageConfig.subpages || {}).length > 0
                ) {
                  return renderNavItem(pageKey, pageConfig);
                }

                return showAsButton ? (
                  <Button
                    key={pageKey}
                    component={Link}
                    to={`/${pageKey}`}
                    variant="contained"
                    color={
                      navConfig.buttonColor === 'secondary'
                        ? 'secondary'
                        : 'primary'
                    }
                    sx={{
                      ml: 2,
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor:
                        navConfig.buttonColor &&
                        navConfig.buttonColor.startsWith('#')
                          ? navConfig.buttonColor
                          : undefined,
                    }}
                  >
                    {visiblePages[pageKey].menutitle}
                  </Button>
                ) : (
                  <Button
                    key={pageKey}
                    component={Link}
                    to={`/${pageKey}`}
                    color="inherit"
                    sx={{
                      ml: 2,
                      paddingLeft: 2,
                      paddingRight: 2,
                      borderRadius: 5,
                      fontWeight:
                        selectedPage === `/${pageKey}` ? 'bold' : 'normal',
                    }}
                  >
                    {visiblePages[pageKey].menutitle}
                  </Button>
                );
              })}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        {drawer}
      </Drawer>
    </Box>
  );
};

export default TopNavigation;
